/**
 * `<IMAGSplash />` — Story 16.8 shared IMAG (image-magnification) splash.
 *
 * Renders a 100vw x 100vh dark canvas matching the broadcast Join screen
 * contract (UX-DR31). Used by `<PreLinkPage>` and `<VoterLandingPage>`
 * when the route is opened with `?display=imag`. The Broadcast Join page
 * already implements the same shape inline (Story 12.3); a future cleanup
 * can refactor that page to consume this primitive.
 */
import type { components } from '../api/types';

type Branding = components['schemas']['Branding'];

// Story 18.2 — `'join'` widens the mode union for the unified
// `/e/{eventId}/join` IMAG projection. CTA copy mirrors the broadcast
// Join screen language.
export type IMAGSplashMode = 'submit' | 'vote' | 'join';

export interface IMAGSplashProps {
  eventName: string;
  joinUrl: string;
  qrPngUrl: string;
  mode: IMAGSplashMode;
  branding: Pick<Branding, 'logoUrl'>;
}

const CTA_COPY: Record<IMAGSplashMode, string> = {
  submit: 'Scan to enter the contest',
  vote: 'Scan to vote',
  join: 'Scan to join',
};

export function IMAGSplash({
  eventName,
  joinUrl,
  qrPngUrl,
  mode,
  branding,
}: IMAGSplashProps) {
  const logoUrl = branding.logoUrl || '';
  return (
    <main
      data-testid="imag-splash"
      tabIndex={-1}
      style={{
        width: '100vw',
        height: '100vh',
        background: '#0B0E13',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
        display: 'grid',
        placeItems: 'center',
        cursor: 'none',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-7)',
          padding: 'var(--space-7)',
        }}
      >
        {logoUrl.length > 0 ? (
          <img
            src={logoUrl}
            alt=""
            data-testid="imag-splash-logo"
            style={{
              maxWidth: 240,
              maxHeight: 120,
              objectFit: 'contain',
            }}
          />
        ) : null}
        <h1
          data-testid="imag-splash-event-name"
          style={{
            margin: 0,
            fontSize: 'var(--text-display-lg-size)',
            lineHeight: 'var(--text-display-lg-line)',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {eventName}
        </h1>
        <img
          src={qrPngUrl}
          alt={`QR code to join the event at ${joinUrl}`}
          aria-label={`QR code to join the event at ${joinUrl}`}
          data-testid="imag-splash-qr"
          width={800}
          height={800}
          style={{ display: 'block' }}
        />
        <p
          data-testid="imag-splash-cta"
          style={{
            margin: 0,
            fontSize: 'var(--text-display-md-size)',
            lineHeight: 'var(--text-display-md-line)',
            textAlign: 'center',
          }}
        >
          {CTA_COPY[mode]}
        </p>
        <p
          data-testid="imag-splash-or"
          style={{
            margin: 0,
            fontSize: 'var(--text-base-size)',
            color: 'var(--text-secondary)',
          }}
        >
          or visit
        </p>
        <code
          data-testid="imag-splash-url"
          aria-label={`Visit ${joinUrl}`}
          style={{
            margin: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-display-md-size)',
            lineHeight: 'var(--text-display-md-line)',
            textAlign: 'center',
          }}
        >
          {joinUrl}
        </code>
      </div>
    </main>
  );
}

/**
 * Build the `/qr?url=...&size=<size>&ec=Q` URL that backs the IMAG QR.
 * Story 12.1's public `/qr` endpoint renders the PNG. Default `size`
 * is 800 (IMAG splash). Pass `size=128` for inline admin previews
 * (Story 17.4).
 */
export function buildQrPngUrl(
  joinUrl: string,
  apiBase: string,
  size: number = 800,
): string {
  const encoded = encodeURIComponent(joinUrl);
  return `${apiBase}/api/qr?url=${encoded}&size=${size}&ec=Q`;
}
